import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { errMsg } from "../api";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

export default function Login() {
  const { login, completeLogin } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(null); // pending_token quand le code par email est actif
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    const msg = sessionStorage.getItem("auth_message");
    if (msg) { setInfo(msg); sessionStorage.removeItem("auth_message"); }
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const goHome = (u) => navigate(u.role === "employe" ? "/ma-journee" : "/");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      const r = await login(email, password);
      if (r.requires_code) {
        setPending(r.pending_token);
        setCooldown(60);
        setInfo("Un code de connexion à 6 chiffres a été envoyé à votre adresse email.");
      } else {
        goHome(r.user);
      }
    } catch (err) { setError(errMsg(err)); }
    finally { setLoading(false); }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const r = await api.post("/auth/verify-code", { pending_token: pending, code, remember_device: remember });
      goHome(completeLogin(r.data));
    } catch (err) { setError(errMsg(err)); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    setError("");
    try {
      await api.post("/auth/resend-code", { pending_token: pending });
      setCooldown(60);
      setInfo("Un nouveau code a été envoyé.");
      toast.success("Nouveau code envoyé");
    } catch (err) { setError(errMsg(err)); }
  };

  const inputCls = "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:ring-2 focus:ring-slate-900 focus:outline-none";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Toaster position="top-right" richColors />
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="text-2xl font-semibold text-slate-900 tracking-tight">Saphir Immo SA</div>
          <div className="text-slate-500 text-sm mt-1">Conciergerie · Blanchisserie · Nettoyage</div>
        </div>
        {!pending ? (
          <form onSubmit={submit} className="bg-white rounded-lg border border-slate-200 p-8 space-y-5" data-testid="login-form">
            <h1 className="text-base font-semibold text-slate-900">Connexion</h1>
            {info && <div className="bg-sky-50 border border-sky-200 text-sky-800 text-sm rounded-lg px-3 py-2" data-testid="login-info">{info}</div>}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Email</label>
              <input
                data-testid="login-email-input"
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                placeholder="vous@exemple.ch"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Mot de passe</label>
              <input
                data-testid="login-password-input"
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit" disabled={loading}
              className="w-full bg-slate-900 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Connexion…" : "Se connecter"}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="bg-white rounded-lg border border-slate-200 p-8 space-y-5" data-testid="login-code-form">
            <h1 className="text-base font-semibold text-slate-900">Code de connexion</h1>
            {info && <div className="bg-sky-50 border border-sky-200 text-sky-800 text-sm rounded-lg px-3 py-2" data-testid="login-info">{info}</div>}
            {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Code à 6 chiffres</label>
              <input
                data-testid="login-code-input"
                type="text" inputMode="numeric" autoComplete="one-time-code" required maxLength={9}
                value={code} onChange={(e) => setCode(e.target.value)}
                className={`${inputCls} tracking-[0.4em] text-center font-mono`} placeholder="000000"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input data-testid="remember-device-checkbox" type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              Mémoriser cet appareil 30 jours
            </label>
            <button
              data-testid="login-code-submit"
              type="submit" disabled={loading}
              className="w-full bg-slate-900 text-white rounded-lg py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50"
            >
              {loading ? "Vérification…" : "Valider le code"}
            </button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" data-testid="resend-code-button" onClick={resend} disabled={cooldown > 0}
                className="text-slate-600 hover:text-slate-900 font-medium disabled:text-slate-400">
                {cooldown > 0 ? `Renvoyer le code (${cooldown} s)` : "Renvoyer le code"}
              </button>
              <button type="button" data-testid="login-back-button"
                onClick={() => { setPending(null); setCode(""); setError(""); setInfo(""); }}
                className="text-slate-400 hover:text-slate-900">Retour</button>
            </div>
            <p className="text-[11px] text-slate-400">Le code est valable 10 minutes. Administrateur : un code de secours peut être utilisé à la place du code email.</p>
          </form>
        )}
      </div>
    </div>
  );
}
